// ============================================================================
// animShared.js
// ============================================================================
//
// Initial visibility system for animation cues (rotate, scale, o2p, color).
// Controls how elements appear before their animation begins.
//
// INIT OPTIONS (DSL):
//   show                         → visible immediately (default)
//   hide                         → invisible until triggered
//   ghost                        → semi-transparent, no interaction
//   fadein(beats)                → fade from invisible to full opacity
//   armed                        → ghost at 0.7, click to start animation
//   armed(opacity)               → ghost at given opacity, click to start
//   armed(opacity, beats)        → ghost at given opacity with fade, click to start
//
// All durations are in beats of tempo(), consistent with dur and tdelay.
//
// Timing is controlled separately by trig and tdelay:
//   trig:auto                    → arm on load (default)
//   trig:playhead                → arm when playhead reaches element
//   tdelay:5                     → delay before animation starts (after click)
//
// ARMED LIFECYCLE:
//   REGISTERED → invisible, handlers installed, waiting for trigger
//   ARMED      → ghost opacity, waiting for performer click
//   RUNNING    → full opacity, animation playing
//   PAUSED     → ghost opacity, animation paused (click to resume)
//
// ============================================================================

// ============================================================================
// ARM SYNC — broadcast armed state changes to other clients
//
// Local clicks send state via WebSocket. Remote clients receive via
// socket.js (oscilla:arm-sync CustomEvent) and apply the transition.
// The _remoteSync flag prevents echo loops.
//
// What syncs: armed->running (start), running->paused (pause),
//             paused->running (resume, with resumeT from pause-drag)
//             Late-joining clients (via anim_state_snapshot from server)
//
// Synced elsewhere:
//   - Continuous drift correction: animSync.js (leader broadcasts positions)
//   - controlXY handle drag: pos_sync in controlXY.js
// ============================================================================

function sendArmSync(uid, newState, extra = {}) {
    const sock = window.socket;
    if (!sock || sock.readyState !== WebSocket.OPEN) return;
    sock.send(JSON.stringify({ type: "arm_sync", uid, state: newState, ...extra }));
}

export function applyRemoteArmSync(uid, newState, extra = {}) {
    const entry = window.oscillaAnimRegistry?.[uid];
    if (!entry) return;

    const { el, cfg } = entry;
    if (!el || !cfg) return;

    const currentState = cfg._armState;
    cfg._remoteSync = true;

    // Handle non-armed animations (late-join sync or remote start)
    // Check for both resumeT and startT (startT is sent on initial start)
    const syncT = extra.resumeT ?? extra.startT ?? null;
    if (!cfg._armed && syncT != null && newState === "running") {
        cfg._dragResumeT = syncT;
        cfg._armState = "running";
        if (typeof cfg._start === "function") {
            cfg._start();
        }
        cfg._remoteSync = false;
        return;
    }

    // Also handle starting animations that aren't armed (auto-start animations)
    // If remote says "running" and we're not running, start the animation
    if (!cfg._armed && newState === "running" && cfg._armState !== "running") {
        cfg._armState = "running";
        if (typeof cfg._start === "function") {
            cfg._start();
        }
        cfg._remoteSync = false;
        return;
    }

    // Armed animations - require armed flag
    if (!cfg._armed) {
        cfg._remoteSync = false;
        return;
    }

    if (currentState === newState) {
        cfg._remoteSync = false;
        return;
    }

    if (newState === "running" && (currentState === "armed" || currentState === "registered")) {
        cfg._armState = "running";
        cfg._startBlocked = false;
        el.style.transition = "opacity 0.4s ease";
        forceReflow(el);
        el.style.opacity = "1";
        if (extra.resumeT != null) cfg._dragResumeT = extra.resumeT;
        if (typeof cfg._start === "function") cfg._start();
    }
    else if (newState === "paused" && currentState === "running") {
        cfg._armState = "paused";
        el.style.transition = "opacity 0.4s ease";
        forceReflow(el);
        el.style.opacity = String(cfg._armOpacity ?? 0.3);
        const ra = window.runningAnimations?.get(uid);
        if (ra?.pause) { ra.pause(); }
        else if (cfg._anim) { cfg._anim.pause(); }
    }
    else if (newState === "running" && currentState === "paused") {
        cfg._armState = "running";
        el.style.transition = "opacity 0.4s ease";
        forceReflow(el);
        el.style.opacity = "1";
        if (extra.resumeT != null) {
            cfg._dragResumeT = extra.resumeT;
            const ra = window.runningAnimations?.get(uid);
            if (ra?.stop) { ra.stop(); }
            else if (cfg._anim?.pause) { cfg._anim.pause(); }
            if (typeof cfg._start === "function") { cfg._start(); }
        } else {
            const ra = window.runningAnimations?.get(uid);
            if (ra?.resume) { ra.resume(); }
            else if (cfg._anim) { cfg._anim.play(); }
            else if (typeof cfg._start === "function") { cfg._start(); }
        }
    }

    cfg._remoteSync = false;
}

window.addEventListener("oscilla:arm-sync", (e) => {
    const { uid, state, ...extra } = e.detail || {};
    if (uid && state) applyRemoteArmSync(uid, state, extra);
});

// ============================================================================
// ANIMATION SYNC — imported from dedicated module
// See animSync.js for late-join handling and periodic drift correction
// ============================================================================
import { becomeLeader } from './animSync.js';
import { handleCueTrigger } from './cueDispatcher.js';
import { secs } from './beats.js';
import { repositionAllElementOverlays } from './button.js';
import { repositionAllHitLabels, repositionAllO2PPathSelectors } from './animOverlays.js';

function forceReflow(el) {
    void el.getBoundingClientRect();
}

function num(val, fallback) {
    return (val != null) ? Number(val) : fallback;
}

// ============================================================================
// applyPrestateBeforeStart — called once at registration time
// ============================================================================

export function applyPrestateBeforeStart(el, cfg) {
    if (cfg._initRegistered) return;
    cfg._initRegistered = true;

    const p = cfg.init;

    // ------------------------------------------------------------------
    // Function-form: armed(...), fadein(...)
    // ------------------------------------------------------------------
    if (p && typeof p === "object" && (p.type === "funcCall" || p.type === "func")) {

        if (p.name === "fadein") {
            // the fade is in beats; the 1 s default is not
            cfg._fadeIn = p.args?.[0] != null ? Number(secs(p.args[0])) : 1;
            cfg._armState = "registered";
            cfg._fadeInTriggered = false;
            el.style.opacity = "0";
            el.style.transition = "opacity 0s";
            return;
        }

        if (p.name === "armed") {
            const args = p.args || [];
            const opacity = Math.max(0, Math.min(1, num(args[0], 0.7)));
            const fade = num(secs(args[1]), 0.3);    // beats, as given

            cfg._armed = true;
            cfg._armOpacity = opacity;
            cfg._armFade = fade;
            cfg._startBlocked = true;

            el.style.pointerEvents = "all";
            installArmedHandlers(el, cfg);

            // trig:auto → arm immediately (visible at ghost opacity)
            // tdelay only delays the animation start, not the arming
            // trig:playhead/edge → defer until playhead reaches element
            const trigAuto = !cfg.trig || cfg.trig === "auto" || cfg.trig === "osc";

            if (trigAuto) {
                cfg._armState = "armed";
                el.style.transition = `opacity ${fade}s ease`;
                el.style.opacity = String(opacity);
                return;
            }

            // Deferred: invisible until trig fires
            cfg._armState = "registered";
            el.style.opacity = "0";
            el.style.transition = "opacity 0s";
            return;
        }
    }

    // ------------------------------------------------------------------
    // String-form
    // ------------------------------------------------------------------
    switch (p) {
        case "armed":
            cfg._armed = true;
            cfg._armOpacity = 0.7;
            cfg._armFade = 0.3;
            cfg._startBlocked = true;

            el.style.pointerEvents = "all";
            installArmedHandlers(el, cfg);

            {
                const trigAuto = !cfg.trig || cfg.trig === "auto" || cfg.trig === "osc";

                if (trigAuto) {
                    cfg._armState = "armed";
                    el.style.transition = "opacity 0.3s ease";
                    el.style.opacity = "0.7";
                } else {
                    cfg._armState = "registered";
                    el.style.opacity = "0";
                    el.style.transition = "opacity 0s";
                }
            }
            return;

        case "hide":
            cfg._armState = "registered";
            el.style.opacity = "0";
            el.style.transition = "opacity 0s";
            return;

        case "ghost":
            cfg._armState = "armed";
            el.style.opacity = "0.3";
            el.style.transition = "opacity 0s";
            return;

        case "show":
        default:
            cfg._armState = "running";
            el.style.opacity = "1";
            el.style.transition = "opacity 0s";
            return;
    }
}

// ============================================================================
// armElement — transition REGISTERED → ARMED
// ============================================================================

export function armElement(el, cfg) {
    if (!cfg._armed) return false;
    if (cfg._armState !== "registered") return false;

    cfg._armState = "armed";
    el.style.transition = `opacity ${cfg._armFade}s ease`;
    forceReflow(el);
    el.style.opacity = String(cfg._armOpacity);
    return true;
}

// ============================================================================
// applyPrestateOnStart — called when animation is triggered
// ============================================================================

export function applyPrestateOnStart(el, cfg, source = "unknown") {

    if (cfg._armed) {
        if (cfg._armState === "running" || cfg._armState === "paused") return;
        armElement(el, cfg);
        return;
    }

    if (cfg._fadeIn) {
        if (cfg._fadeInTriggered) return;
        const isScrollMode = window.mode === "scroll";
        if (isScrollMode && source === "unknown" && !cfg.fromCueTrigger) return;
        cfg._fadeInTriggered = true;
        el.style.transition = `opacity ${cfg._fadeIn}s ease`;
        forceReflow(el);
        el.style.opacity = "1";
        return;
    }

    if (cfg.init === "ghost") {
        el.style.opacity = "0.3";
        return;
    }

    if (cfg.init === "hide") {
        el.style.opacity = "1";
        return;
    }

    el.style.opacity = "1";
}

// ============================================================================
// Armed click handlers
// ============================================================================

function installArmedHandlers(el, cfg) {
    if (cfg._clickHandler) return;

    cfg._clickHandler = (e) => {
        if (e.detail?.kind && cfg.kind && e.detail.kind !== cfg.kind) return;

        const state = cfg._armState;
        if (state === "registered") return;

        const isLocal = !cfg._remoteSync;

        if (state === "armed") {
            cfg._armState = "running";
            cfg._startBlocked = false;
            el.style.transition = "opacity 0.4s ease";
            forceReflow(el);
            el.style.opacity = "1";
            if (typeof cfg._start === "function") cfg._start();
            if (isLocal) {
                // Client who starts animation becomes leader for sync
                becomeLeader();
                sendArmSync(cfg.uid, "running", {
                    dur: cfg.dur,
                    startT: cfg.startPos ?? 0
                });
            }
            return;
        }

        if (state === "running") {
            cfg._armState = "paused";
            el.style.transition = "opacity 0.4s ease";
            forceReflow(el);
            el.style.opacity = String(cfg._armOpacity ?? 0.3);
            const ra = window.runningAnimations?.get(cfg.uid);
            if (ra?.pause) { ra.pause(); }
            else if (cfg._anim) { cfg._anim.pause(); }
            if (isLocal) sendArmSync(cfg.uid, "paused");
            return;
        }

        if (state === "paused") {
            cfg._armState = "running";
            el.style.transition = "opacity 0.4s ease";
            forceReflow(el);
            el.style.opacity = "1";

            if (cfg._needsRestart) {
                // Params changed via live console — recreate animation
                delete cfg._needsRestart;
                if (typeof cfg._start === "function") { cfg._start(); }
            } else {
                const ra = window.runningAnimations?.get(cfg.uid);
                if (ra?.resume) { ra.resume(); }
                else if (cfg._anim) { cfg._anim.play(); }
                else if (typeof cfg._start === "function") { cfg._start(); }
            }

            if (isLocal) {
                becomeLeader();
                sendArmSync(cfg.uid, "running", {
                    resumeT: cfg._resumeT ?? undefined
                });
            }
            delete cfg._resumeT;
            return;
        }
    };

    installOscToggleHandler(el, cfg);
    installToggleStatesHandler(el, cfg);
    el.addEventListener("oscilla-hit", cfg._clickHandler);
    el.style.pointerEvents = "all";

    if (cfg.trig === 'osc' && cfg.oscAddr) {
        const addr = cfg.oscAddr;
        const oscHandler = ({ detail: { address } }) => {
            if (address !== addr) return;
            el.dispatchEvent(new CustomEvent('oscilla-hit'));
        };
        window.addEventListener('oscilla:osc-in', oscHandler);
        cfg._oscTrigUnbind = () => window.removeEventListener('oscilla:osc-in', oscHandler);
    }
}

// ============================================================================
// OSC toggle handler
// Only installs when oscToggle:1 is specified in the cue
// ============================================================================

export function installOscToggleHandler(el, cfg) {
    // Only enable toggle feature if oscToggle:1 is specified
    if (!cfg.oscToggle) return;

    if (cfg._oscToggleHandler) return;

    cfg._oscToggleHandler = (e) => {
        if (!e.detail?.uid || e.detail.uid !== cfg.uid) return;
        if (e.detail.kind && cfg.kind && e.detail.kind !== cfg.kind) return;

        const oscEnabled = e.detail?.oscEnabled ?? false;
        cfg._oscEnabled = oscEnabled;
        if (cfg.oscCfg) cfg.oscCfg.enabled = oscEnabled;
        cfg.osc = oscEnabled ? 1 : 0;

        // Immediately broadcast OSC toggle state to other clients
        const sock = window.socket;
        if (sock && sock.readyState === WebSocket.OPEN) {
            const clientColor = window.localClientColor || localStorage.getItem("clientColor") || "#ffaa00";
            sock.send(JSON.stringify({
                type: "osc_toggle_sync",
                uid: cfg.uid,
                oscEnabled,
                clientColor
            }));
        }
    };

    el.addEventListener("oscilla-osc-toggle", cfg._oscToggleHandler);
}

// ============================================================================
// Toggle states handler (decoupled from OSC)
// Installs when toggleStates is specified in the cue
// ============================================================================

export function installToggleStatesHandler(el, cfg) {
    // Only enable if toggleStates is specified
    if (!cfg.toggleStates || cfg.toggleStates.length === 0) return;

    if (cfg._toggleStatesHandler) return;

    cfg._toggleStatesHandler = (e) => {
        if (!e.detail?.uid || e.detail.uid !== cfg.uid) return;
        if (e.detail.kind && cfg.kind && e.detail.kind !== cfg.kind) return;

        const { stateIndex, stateName, color, onToggle } = e.detail;

        // Update config state
        cfg._toggleStateIndex = stateIndex;
        cfg._toggleColor = color;

        // Broadcast toggle state to other clients
        const sock = window.socket;
        if (sock && sock.readyState === WebSocket.OPEN) {
            sock.send(JSON.stringify({
                type: "toggle_state_sync",
                uid: cfg.uid,
                stateIndex,
                stateName,
                color
            }));
        }

        // Get client index from position in playbackClientNames list (1-based)
        const myName = localStorage.getItem("clientName") || "";
        const clientList = window.oscillaPrefs?.playbackClientNames || window.savedClientNames || [];
        let clientIndex = clientList.indexOf(myName);
        // Convert to 1-based (instrument 1 = index 0 in array)
        clientIndex = clientIndex >= 0 ? clientIndex + 1 : 0;

        // Send OSC directly for toggle state changes
        if (onToggle) {
            // Parse onToggle to extract OSC address if it's an osc() expression
            const oscMatch = onToggle.match(/osc\s*\(\s*addr:\s*\/?([^,\s)]+)/);
            if (oscMatch) {
                // Without its leading slash: the server sends it as /<addr> (no /oscilla/ prefix)
                const oscAddr = oscMatch[1].replace(/^\//, '');
                // Send toggle_claim message with typed args (server handles string/int types)
                const sock = window.socket;
                if (sock && sock.readyState === WebSocket.OPEN) {
                    sock.send(JSON.stringify({
                        type: "toggle_claim",
                        addr: oscAddr,
                        uid: cfg.uid || "",
                        state: stateIndex,
                        client: clientIndex,
                        timestamp: Date.now()
                    }));
                }
            } else {
                // Fallback: try to execute as cue expression with placeholders
                let expr = onToggle;
                expr = expr.replace(/\$state/g, stateIndex);
                expr = expr.replace(/\$stateName/g, stateName || "");
                expr = expr.replace(/\$uid/g, cfg.uid || "");
                expr = expr.replace(/\$color/g, color || "");
                expr = expr.replace(/\$clientIndex/g, clientIndex);

                try {
                    handleCueTrigger(expr, false, true);
                } catch (err) {
                    console.error("[toggleStates] onToggle error:", err);
                }
            }
        }
    };

    el.addEventListener("oscilla-toggle-state", cfg._toggleStatesHandler);
}

// ============================================================================
// Query helpers
// ============================================================================

export function needsArming(cfg) {
    if (!cfg._armed) return false;
    return cfg._armState === "registered";
}

export function needsFadeIn(cfg) {
    if (!cfg._fadeIn) return false;
    return !cfg._fadeInTriggered;
}

export function triggerFadeIn(el, cfg) {
    if (!cfg._fadeIn || cfg._fadeInTriggered) return false;
    cfg._fadeInTriggered = true;
    el.style.transition = `opacity ${cfg._fadeIn}s ease`;
    forceReflow(el);
    el.style.opacity = "1";
    return true;
}

// ============================================================================
// Reset
// ============================================================================

export function resetArmedState(el, cfg) {
    if (!cfg) return;

    if (cfg._armed) {
        cfg._armState = "registered";
        cfg._startBlocked = true;
        el.style.transition = "opacity 0.2s ease";
        el.style.opacity = "0";
    }

    if (cfg._fadeIn) {
        cfg._fadeInTriggered = false;
        el.style.transition = "opacity 0.2s ease";
        el.style.opacity = "0";
    }

    if (cfg._anim) {
        cfg._anim.pause();
        cfg._anim = null;
    }
}

export function resetAllAnimationStates(options = {}) {
    const { broadcastToServer = true, resetFaders = true } = options;

    const registry = window.oscillaAnimRegistry;
    if (!registry) return;

    // Stop all animations and reset to initial state
    for (const uid of Object.keys(registry)) {
        const entry = registry[uid];
        if (!entry) continue;
        const { el, cfg } = entry;
        if (el && cfg) {
            cfg._initRegistered = false;
            cfg._currentT = 0;
            cfg._currentScale = null;
            resetArmedState(el, cfg);

            // Reset transforms for scale elements
            if (cfg.kind === "scale" && cfg._scaleWrapper) {
                cfg._scaleWrapper.style.transform = "scale(1, 1)";
            }

            // Reset o2p elements to start position
            if (cfg.kind === "o2p" && cfg._wrapper) {
                // Will be repositioned on next start
            }
        }
    }

    // Clear fader positions
    if (resetFaders && window._o2pTouchGroups) {
        for (const group of Object.values(window._o2pTouchGroups)) {
            for (const fader of Object.values(group.faders || {})) {
                if (fader.curT !== undefined) {
                    fader.curT = 0.5; // Reset to middle
                }
            }
        }
    }

    // Tell server to clear stored animation states
    if (broadcastToServer) {
        const sock = window.socket;
        if (sock && sock.readyState === WebSocket.OPEN) {
            sock.send(JSON.stringify({ type: "reset_all_states" }));
        }
    }

    // Clear localStorage OSC states
    try {
        localStorage.removeItem("oscilla.oscToggleState");
    } catch (e) { /* ignore */ }
}

// Initialize reset button handler
function initResetButton() {
    const btn = document.getElementById("reset-anims-button");
    if (!btn) return;

    btn.addEventListener("click", () => {
        if (confirm("Reset all animations to initial state?")) {
            resetAllAnimationStates();
            // Reload page to fully reinitialize
            window.location.reload();
        }
    });
}

// Set up button when DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initResetButton);
} else {
    initResetButton();
}

// ============================================================================
// SVG animation wrapper
// ============================================================================

// Animation wrappers are NOT promoted to compositor layers. A
// will-change:transform hint makes Chrome give every animated member its own
// GPU texture, rasterised once and then stretched by the member's own scale()
// and by the zoom -- blurry, and at 4x zoom awful. Without the hint the
// transform is applied at paint time, as vectors, crisp at any scale.
export const COMPOSITE_ANIM_WRAPPERS = false;
export function hintWrapperCompositing(wrapper) {
    if (!wrapper?.style) return;
    wrapper.style.willChange = COMPOSITE_ANIM_WRAPPERS ? "transform" : "";
}

/**
 * A wrapper of its own for one kind of transform cue, nested inside the base
 * animation wrapper so the two transforms multiply rather than overwrite.
 * @param {Element} el
 * @param {string} kind  e.g. "rotate"
 */
function ensureKindWrapper(el, kind) {
    if (!el) return el;
    const key = `_oscillaAnimWrapper_${kind}`;
    if (el[key] && el[key].isConnected) return el[key];
    const base = ensureAnimWrapper(el);          // scale's frame, and the parent
    if (!base || base === el) return base || el;
    const cls = `oscilla-anim-${kind}`;
    let wrapper = base.querySelector(`:scope > g.${cls}`);
    if (!wrapper) {
        wrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
        wrapper.classList.add(cls);
        hintWrapperCompositing(wrapper);
        while (base.firstChild) wrapper.appendChild(base.firstChild);
        base.appendChild(wrapper);
    }
    el[key] = wrapper;
    return wrapper;
}

export function ensureAnimWrapper(el, kind = "") {
    // A second transform cue on the same element needs its own <g>. scale()
    // and rotate() both assign style.transform outright, so with one shared
    // wrapper the later writer would win every frame and the other cue would
    // not show. Nesting composes them instead: the base wrapper carries
    // scale, an inner "oscilla-anim-<kind>" carries rotate.
    if (kind) return ensureKindWrapper(el, kind);
    if (!el || el._oscillaAnimWrapper) return el._oscillaAnimWrapper || el;
    if (!(el instanceof SVGGElement)) {
        // A leaf (path, rect, ...) can't hold a child <g>, and setting CSS
        // transform on it would override its own transform="translate(...)"
        // attribute (Inkscape writes one whenever a shape is moved) — the
        // shape would jump the moment the animation starts. Wrap it in a
        // parent <g> instead and animate that.
        const parent = el.parentElement;
        if (!parent) return el;
        // Adopt a parent wrapper only when it holds this leaf alone (and what
        // the leaf carries). One holding siblings is a GROUP's base wrapper:
        // a kind wrapper built in it would swallow them all, and the leaf's
        // rotate would turn the whole group (o2p.js makes the same test).
        const carried = el._carry?.() || [];
        const ownsParent = parent.classList?.contains("oscilla-anim")
            && [...parent.children].every(c => c === el || carried.includes(c));
        let wrapper = ownsParent ? parent : null;
        if (!wrapper) {
            wrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
            wrapper.classList.add("oscilla-anim");
            parent.insertBefore(wrapper, el);
            wrapper.appendChild(el);
            // a pivot authored on the leaf is the wrapper's to turn about
            // (rotate/scale look for it from the wrapper upward)
            if (el.dataset?.pivot) wrapper.dataset.pivot = el.dataset.pivot;
            // What is drawn beside it and must move with it comes along: a
            // console sound's box keeps its scope traces and resize handles
            // next to it, not inside (audioFile.js sets _carry).
            for (const c of el._carry?.() || []) if (c?.parentNode) wrapper.appendChild(c);
        }
        hintWrapperCompositing(wrapper);
        el._oscillaAnimWrapper = wrapper;
        return wrapper;
    }

    // In-place transforms (rotate / scale / colour) must nest INSIDE an o2p
    // positioning wrapper, so path-following stays the OUTER frame. Put a
    // rotate on the outside and it spins the already-positioned object around
    // the path's start point instead of in place — the object appears to orbit
    // the path rather than travel along it. So host this wrapper inside the o2p
    // positioning wrapper when one exists; otherwise directly inside el.
    const host = el.querySelector(":scope > g.oscilla-o2p-anim") || el;

    const existing = host.querySelector(":scope > g.oscilla-anim");
    if (existing) {
        el._oscillaAnimWrapper = existing;
        hintWrapperCompositing(existing);
        return existing;
    }

    const wrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
    wrapper.classList.add("oscilla-anim");
    hintWrapperCompositing(wrapper);
    while (host.firstChild) wrapper.appendChild(host.firstChild);
    host.appendChild(wrapper);
    el._oscillaAnimWrapper = wrapper;
    return wrapper;
}

// ============================================================================
// OSC enable helper
// ============================================================================

export function isOscEnabled(cfg, oscMode = 0) {
    if (!cfg) return false;
    if (cfg._oscEnabled === true) return true;
    if (cfg._oscEnabled === false) return false;
    return Number(oscMode) > 0;
}

// ============================================================================
// Element Visibility Helpers
// Hide/show SVG elements + their associated HTML overlays (hit labels, OSC, etc.)
// ============================================================================

/**
 * Hide an element by uid (SVG + all associated HTML overlays)
 * @param {string} uid - Element uid
 * @param {Object} options - { dur: transition duration in seconds }
 */
export function hideElement(uid, options = {}) {
    setElementVisibility(uid, false, options);
}

/**
 * Show an element by uid (SVG + all associated HTML overlays)
 * @param {string} uid - Element uid
 * @param {Object} options - { dur: transition duration in seconds }
 */
export function showElement(uid, options = {}) {
    setElementVisibility(uid, true, options);
}

/**
 * Toggle visibility of an element by uid
 * @param {string} uid - Element uid
 * @param {Object} options - { dur: transition duration in seconds }
 * @returns {boolean} New visibility state
 */
export function toggleElement(uid, options = {}) {
    const found = findElementByUid(uid);
    if (!found?.el) {
        console.warn(`[visibility] Element not found: ${uid}`);
        return false;
    }

    const el = found.el;
    const isVisible = el.style.display !== "none" &&
                      el.style.visibility !== "hidden";
    const newState = !isVisible;
    // Toggle only changes visibility - do NOT pass sourceEl to avoid repositioning
    setElementVisibility(uid, newState, { dur: options.dur });
    return newState;
}

/**
 * Find an element by uid - checks registry first, then searches DOM for uid:xxx in id
 * @param {string} uid - The uid to search for
 * @returns {Object|null} { el, cfg, source } or null if not found
 */
export function findElementByUid(uid) {
    // 1. Check animation registry first
    const entry = window.oscillaAnimRegistry?.[uid];
    if (entry?.el) {
        return { el: entry.el, cfg: entry.cfg, source: 'registry' };
    }

    // 2. Search DOM for elements with data-oscilla-uid attribute (set by drag, rotate, etc.)
    // This is the preferred method as preprocessors set this attribute explicitly
    // Search in SVG root first (where score elements live), then fall back to document
    const scoreInner = document.getElementById("scoreInner");
    const singlePageContent = document.getElementById("singlePage-content");
    const svgRoot = scoreInner?.querySelector("svg") ||
                    singlePageContent?.querySelector("svg") ||
                    document.querySelector("#scrollStage svg") ||
                    document.querySelector("svg");

    const searchRoots = [];
    if (svgRoot) searchRoots.push(svgRoot);
    if (scoreInner) searchRoots.push(scoreInner);
    if (singlePageContent) searchRoots.push(singlePageContent);
    searchRoots.push(document);

    // First try data-oscilla-uid attribute (most reliable)
    for (const root of searchRoots) {
        const el = root.querySelector(`[data-oscilla-uid="${uid}"]`);
        if (el) {
            return { el, cfg: null, source: 'data-attr' };
        }
    }

    // Fallback: search for uid:xxx in id attribute
    // EXCLUDE ui() cue elements - those are control triggers, not targets
    const selector = `[id*="uid:${uid}"], [data-oscilla*="uid:${uid}"]`;
    for (const root of searchRoots) {
        const candidates = root.querySelectorAll(selector);
        for (const el of candidates) {
            const id = el.getAttribute('data-oscilla') || el.id || '';
            // Skip control cues - they reference targets, they ARE NOT targets themselves
            if (id.startsWith('ui(') || id.includes(' ui(') ||
                id.startsWith('button(') || id.includes(' button(')) {
                continue;
            }
            // The CSS [id*=...] is a substring match — guard against partial uid
            // hits (e.g. 'chfx' matching 'chfx_reverb') by extracting the actual
            // uid value and comparing exactly.
            const uidMatch = id.match(/uid:\s*([^,)\s]+)/);
            if (!uidMatch || uidMatch[1] !== uid) continue;
            return { el, cfg: null, source: 'dom' };
        }
    }

    // 3. Also try exact id match (for elements with simple ids)
    const exactEl = document.getElementById(uid);
    if (exactEl) {
        return { el: exactEl, cfg: null, source: 'id' };
    }

    return null;
}

/**
 * Position target element relative to source element (bottom-right of source)
 * Updates the target's SVG transform to place it at source's bottom-right corner
 */
function positionRelativeToSource(targetEl, sourceEl) {
    if (!targetEl || !sourceEl) return;

    try {
        // Get source bounding box in SVG coordinates
        const sourceBBox = sourceEl.getBBox();
        const sourceCTM = sourceEl.getCTM();

        // Get target's parent CTM to convert to local coordinates
        const targetParent = targetEl.parentElement;
        const targetParentCTM = targetParent?.getCTM?.();

        if (!sourceCTM || !targetParentCTM) {
            console.warn("[visibility] Could not get CTM for positioning");
            return;
        }

        // Calculate source's bottom-right corner in SVG root coordinates
        const sourceBottomRightX = sourceBBox.x + sourceBBox.width;
        const sourceBottomRightY = sourceBBox.y + sourceBBox.height;

        // Transform to SVG root coordinates
        const svg = sourceEl.ownerSVGElement;
        if (!svg) return;

        const pt = svg.createSVGPoint();
        pt.x = sourceBottomRightX;
        pt.y = sourceBottomRightY;
        const rootPt = pt.matrixTransform(sourceCTM);

        // Transform from root to target's parent coordinate space
        const invTargetParentCTM = targetParentCTM.inverse();
        const localPt = rootPt.matrixTransform(invTargetParentCTM);

        // Add small offset (10px gap)
        const offsetX = 10;
        const offsetY = 10;

        // Set target's transform
        targetEl.setAttribute("transform", `translate(${localPt.x + offsetX}, ${localPt.y + offsetY})`);

        // A frame later, the HTML that follows SVG elements follows this one:
        // its buttons and click / drag overlays, hit labels, o2p path
        // selectors, and its popup if it is open
        requestAnimationFrame(() => {
            repositionAllElementOverlays();
            repositionAllHitLabels();
            repositionAllO2PPathSelectors();
            import('../interaction/popupOverlay.js').then(({ repositionAllPopups }) => repositionAllPopups());
        });
    } catch (err) {
        console.warn("[visibility] Error positioning element:", err);
    }
}

/**
 * Set visibility of element + all associated overlays
 * options.local: this client's view only — not saved to ui_state.json and
 * not broadcast (a role-based view must not rewrite everyone else's).
 */
function setElementVisibility(uid, visible, options = {}) {
    const { dur = 0, sourceEl = null, local = false } = options;

    // 1. Find SVG element (registry or DOM search)
    const found = findElementByUid(uid);
    const entry = window.oscillaAnimRegistry?.[uid]; // Still need for cfg

    if (found?.el) {
        const el = found.el;
        const cfg = found.cfg || entry?.cfg;

        // Position relative to source element if showing and sourceEl provided
        if (visible && sourceEl) {
            positionRelativeToSource(el, sourceEl);
        }

        // A hide still waiting to set display:none would undo this change
        if (el._oscillaHideTimer) { clearTimeout(el._oscillaHideTimer); el._oscillaHideTimer = null; }
        if (dur > 0) {
            el.style.transition = `opacity ${dur}s ease`;
            if (visible) {
                // Coming back from display:none there is nothing to fade from:
                // start it at 0, let a frame render that, then fade up.
                // (el.offsetHeight is undefined on SVG, so it cannot force the reflow.)
                const wasHidden = el.style.display === "none" || el.style.visibility === "hidden";
                if (wasHidden) el.style.opacity = "0";
                el.style.display = "";
                el.style.visibility = "visible";
                requestAnimationFrame(() => requestAnimationFrame(() => { el.style.opacity = "1"; }));
            } else {
                el.style.opacity = "0";
                el._oscillaHideTimer = setTimeout(() => {
                    el._oscillaHideTimer = null;
                    el.style.display = "none";
                }, dur * 1000);
            }
        } else {
            el.style.display = visible ? "" : "none";
            el.style.visibility = visible ? "visible" : "hidden";
            el.style.opacity = visible ? "1" : "0";
        }

        // Store visibility state
        if (cfg) cfg._hidden = !visible;

        // Handle OSC overlay
        if (cfg?._overlay?.box) {
            cfg._overlay.box.style.display = visible ? "" : "none";
        }
    }

    // 2. Find and hide/show hit labels
    //    Match the exact uid AND any hit label whose groupEl lives inside
    //    the hidden element — otherwise nested o2p hits stay visible when
    //    a parent (e.g. rot_triangleA) is toggled off.
    const hideHitLabel = (rec) => {
        if (rec.div) rec.div.style.display = visible ? "" : "none";
        if (rec.hit) rec.hit.style.display = visible ? "" : "none";
        if (rec.valueLabel) rec.valueLabel.style.display = visible ? "" : "none";
    };
    for (const rec of (window._oscillaHitLabels || [])) {
        if (rec.uid === uid) {
            hideHitLabel(rec);
            continue;
        }
        if (found?.el && rec.groupEl && found.el.contains(rec.groupEl)) {
            hideHitLabel(rec);
        }
    }

    // 3. Find and hide/show o2p launcher circles and foreignObjects associated with groups inside this element
    // When toggling a panel (like polygonControls), also toggle any launcher UI that belongs to it
    if (found?.el) {
        // Find all o2p groups inside this element
        const groupIds = new Set();
        const faders = found.el.querySelectorAll('[id*="group:"], [data-oscilla*="group:"]');
        faders.forEach(f => {
            // the DSL is in data-oscilla (the editor's way) or in the id; a
            // propagate member keeps both, so both are read
            const match = `${f.getAttribute("data-oscilla") || ""} ${f.id}`.match(/group:(\w+)/);
            if (match) groupIds.add(match[1]);
        });

        // Toggle control bars for each group
        // Note: Only toggle the control bar, not the preset panel foreignObject.
        // The preset panel should stay hidden unless user explicitly clicks the P button.
        for (const groupId of groupIds) {
            // Control bar HTML overlay
            const controlBar = document.querySelector(`.o2p-control-bar[data-uid="${groupId}"]`);
            if (controlBar) {
                controlBar.style.display = visible ? "flex" : "none";
                // Reposition when showing
                if (visible) {
                    import('../control/o2pLauncher.js').then(mod => {
                        mod.repositionLauncherAndCircle(groupId);
                    });
                }
            }
            // Legacy: launcher circle (SVG) - for backwards compat
            const circle = document.querySelector(`circle.o2p-launcher-circle[data-uid="${groupId}"]`);
            if (circle) {
                circle.style.display = visible ? "" : "none";
            }
            // When hiding, also hide the preset panel foreignObject so it doesn't float orphaned
            if (!visible) {
                const launcher = document.querySelector(`.o2p-launcher[data-uid="${groupId}"]`);
                if (launcher) {
                    const foEl = launcher.closest('foreignObject');
                    if (foEl) foEl.style.display = "none";
                }
            }
        }

        // 4. Find and hide/show all HTML overlays for elements inside this panel
        // These HTML overlays block interaction when the panel is hidden
        const overlayClasses = [
            '.oscilla-drag-overlay',
            '.oscilla-drag-click-overlay',
            '.oscilla-click-overlay'
        ];
        for (const cls of overlayClasses) {
            const overlays = document.querySelectorAll(cls);
            for (const overlay of overlays) {
                const svgEl = overlay._svgElement;
                if (svgEl && found.el.contains(svgEl)) {
                    overlay.style.display = visible ? "" : "none";
                    // Reposition overlay after becoming visible (place() fails when element is hidden)
                    if (visible && overlay._place) {
                        // Delay to ensure CSS display change is applied
                        setTimeout(() => overlay._place(), 50);
                    }
                }
            }
        }
    }

    // Track and sync visibility state (skip if this is a remote sync or view-local)
    if (!_visibilityRemoteSync && !local) {
        trackVisibilityChange(uid, visible);
        broadcastVisibilityChange(uid, visible);
    }

    // Handle popup overlays - elevate drag-html popups above HTML overlays
    // toggleOverlay will auto-register drag-html panels
    import('../interaction/popupOverlay.js').then(({ toggleOverlay }) => {
        toggleOverlay(uid, visible);
    });
}

/**
 * Hide/show multiple elements by uid pattern
 * @param {string} pattern - Regex pattern to match uids
 * @param {boolean} visible - true to show, false to hide
 */
export function setVisibilityByPattern(pattern, visible, options = {}) {
    const regex = new RegExp(pattern);
    const registry = window.oscillaAnimRegistry || {};

    for (const uid of Object.keys(registry)) {
        if (regex.test(uid)) {
            setElementVisibility(uid, visible, options);
        }
    }
}

// Expose on window for console/live console access
window.oscillaVisibility = {
    hide: hideElement,
    show: showElement,
    toggle: toggleElement,
    setByPattern: setVisibilityByPattern,
    saveState: saveVisibilityState,
    loadState: loadVisibilityState
};

// ============================================================================
// VISIBILITY STATE PERSISTENCE
// Save/load visibility states to ui_state.json for prepared starting configs
// ============================================================================

let _visibilityState = {};
let _visibilitySaveTimeout = null;
let _visibilityRemoteSync = false;

/**
 * Get saved visibility state for a uid
 * @returns {boolean|undefined} - true if visible, false if hidden, undefined if no saved state
 */
export function getSavedVisibility(uid) {
    const state = _visibilityState[uid];
    if (state && typeof state.visible === "boolean") {
        return state.visible;
    }
    return undefined;
}

/**
 * Get a snapshot of all visibility states (for configuration save)
 * @returns {Object} - Copy of visibility state { uid: { visible: boolean }, ... }
 */
export function getVisibilitySnapshot() {
    return JSON.parse(JSON.stringify(_visibilityState));
}

/**
 * Apply a visibility state snapshot (for configuration recall)
 * @param {Object} snapshot - Visibility state { uid: { visible: boolean }, ... }
 * @param {number} [dur=0] - Transition duration
 * @param {Object} [opts]
 * @param {boolean} [opts.local] - this client's view only: not recorded in the
 *   saved state, not broadcast (see setElementVisibility)
 */
export function applyVisibilitySnapshot(snapshot, dur = 0, opts = {}) {
    if (!snapshot || typeof snapshot !== "object") return;
    const local = !!opts.local;

    for (const [uid, data] of Object.entries(snapshot)) {
        if (data && typeof data.visible === "boolean") {
            setElementVisibility(uid, data.visible, { dur, local });
            if (!local) _visibilityState[uid] = { visible: data.visible };
        }
    }

    // Dispatch event so toggle indicators can update
    window.dispatchEvent(new CustomEvent("oscilla:visibility-state-loaded"));
}

/**
 * Track visibility change in local state and auto-save
 */
function trackVisibilityChange(uid, visible) {
    _visibilityState[uid] = { visible };
    saveVisibilityState(); // Auto-save (debounced)
}

/**
 * Broadcast visibility change to other clients
 * Respects viewSyncMode: "local" = don't broadcast, "broadcast" = send, "receive" = only receive
 * Also respects presetScope: "user" = don't broadcast, "composition" = broadcast (conductor mode)
 */
function broadcastVisibilityChange(uid, visible) {
    if (_visibilityRemoteSync) return; // Don't echo remote changes

    const presetScope = window.presetScope || 'user';

    // In composition/conductor mode, always broadcast visibility changes
    // In user mode, respect the viewSyncMode setting
    if (presetScope === 'user') {
        // User mode - check viewSyncMode
        if (window.viewSyncMode === "local" || window.viewSyncMode === "receive") {
            return; // Don't broadcast in local/receive modes
        }
    }
    // presetScope === 'composition' = conductor mode, always broadcast

    const sock = window.socket;
    if (!sock || sock.readyState !== WebSocket.OPEN) {
        return;
    }
    sock.send(JSON.stringify({
        type: "visibility_sync",
        uid,
        visible,
        project: window.currentProject
    }));
}

/**
 * Apply remote visibility change
 */
function applyRemoteVisibility(uid, visible) {
    _visibilityRemoteSync = true;
    setElementVisibility(uid, visible, { dur: 0.3 });
    _visibilityRemoteSync = false;
}

// Listen for remote visibility sync
// Respects viewSyncMode: "broadcast" mode ignores incoming (conductor controls, doesn't receive)
// Also respects presetScope: in "user" mode, ignore incoming visibility changes
window.addEventListener("oscilla:visibility-sync", (e) => {
    const presetScope = window.presetScope || 'user';

    if (window.viewSyncMode === "broadcast") {
        return; // Conductor mode: don't apply others' changes
    }

    // Only receive in composition/conductor mode
    if (presetScope === 'user') {
        return; // User mode = local only, ignore incoming
    }

    const { uid, visible } = e.detail || {};
    if (uid !== undefined && visible !== undefined) {
        applyRemoteVisibility(uid, visible);
    }
});

/**
 * Save current visibility state to server (debounced)
 */
function saveVisibilityState() {
    if (_visibilitySaveTimeout) clearTimeout(_visibilitySaveTimeout);
    // the project and the state, taken now: a switch within the second wrote
    // the old project's visibility into the new one's ui-state
    const project = window.currentProject;
    const body = JSON.stringify(_visibilityState);
    _visibilitySaveTimeout = setTimeout(async () => {
        if (!project) return;

        try {
            await fetch(`/api/ui-state/${encodeURIComponent(project)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body
            });
            // State saved successfully
        } catch (err) {
            console.warn("[visibility] Failed to save state:", err);
        }
    }, 1000); // 1 second debounce
}

/**
 * Load visibility state from server and apply
 */
export async function loadVisibilityState() {
    const project = window.currentProject;
    if (!project) return;

    try {
        const response = await fetch(`/api/ui-state/${encodeURIComponent(project)}`);
        if (!response.ok) return;

        const state = await response.json();
        if (!state || typeof state !== "object") return;

        _visibilityState = state;

        // Apply each visibility state
        for (const [uid, data] of Object.entries(state)) {
            if (data && typeof data.visible === "boolean") {
                setElementVisibility(uid, data.visible, { dur: 0 });
            }
        }

        // Dispatch event so toggle indicators can update
        window.dispatchEvent(new CustomEvent("oscilla:visibility-state-loaded"));
    } catch (err) {
        console.warn("[visibility] Failed to load state:", err);
    }
}

// Expose load function globally for project loader. An o2p preset recall
// calls _applyVisibilitySnapshot through window, so an extension can wrap it
// (polygonfield does).
window._loadVisibilityState = loadVisibilityState;
window._getVisibilitySnapshot = getVisibilitySnapshot;
window._applyVisibilitySnapshot = applyVisibilitySnapshot;
